import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRight,
  faBolt,
  faChevronDown,
  faFileShield,
  faFlaskVial,
  faMicrochip,
  faOilCan,
  faShieldHalved,
  faTag,
  faTruckFast,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'

import { Footer, LogoLockup, WA_NUMBER } from '../components/SiteChrome'
import { BrandEasyOrder } from '../components/BrandEasyOrder'
import { BrandTrustSections, type TrustQuestion } from '../components/BrandTrustSections'
import { GloveBrandAlternatives } from '../components/GloveBrandAlternatives'
import { COMET_GLOVE_DEFINITIONS } from '../data/cometGloves'
import type { CometGloveDefinition, CometRiskKey } from '../data/cometGloves'
import { whatsAppCtaProps, trackSelectItem } from '../lib/analytics'
import {
  getProductPrimaryImage,
  getStorefrontConfig,
  listStorefrontCategories,
  listStorefrontProducts,
} from '../lib/dropify'
import type { StorefrontCategory, StorefrontConfig, StorefrontProduct } from '../lib/dropify'
import { INDEXABLE_ROBOTS, absoluteUrl, brandTitle, buildBrandLandingSchema, buildBreadcrumbSchema, buildFaqSchema } from '../lib/seo'
import { getBrandProofImages } from '../lib/testimonials'
import { listDashboardNewsPosts } from '../lib/news'
import type { FaqItem } from '../lib/seo'

const PAGE_PATH = '/sarung-tangan-comet'
const PAGE_CATEGORY = 'Sarung Tangan COMET'

type CometProduct = CometGloveDefinition & { image: string }

const RISK_GROUPS: Array<{
  key: CometRiskKey
  anchor: string
  title: string
  shortTitle: string
  desc: string
  icon: typeof faMicrochip
  accent: string
}> = [
  {
    key: 'presisi',
    anchor: 'comet-presisi',
    title: 'Presisi & ESD',
    shortTitle: 'Presisi',
    desc: 'Untuk inspeksi, perakitan, elektronik, dan komponen yang membutuhkan sensitivitas tangan.',
    icon: faMicrochip,
    accent: 'bg-cyan-400 text-[#071a2d]',
  },
  {
    key: 'minyak',
    anchor: 'comet-minyak',
    title: 'Minyak & benturan',
    shortTitle: 'Minyak',
    desc: 'Untuk perawatan mesin, otomotif, pekerjaan berat, permukaan berminyak, dan risiko benturan.',
    icon: faOilCan,
    accent: 'bg-amber-400 text-[#071a2d]',
  },
  {
    key: 'sayat',
    anchor: 'comet-sayat',
    title: 'Risiko sayatan',
    shortTitle: 'Anti-sayat',
    desc: 'Untuk kaca, keramik, fabrikasi logam, pemotongan, dan material dengan tepi tajam.',
    icon: faShieldHalved,
    accent: 'bg-violet-400 text-[#071a2d]',
  },
  {
    key: 'kimia',
    anchor: 'comet-kimia',
    title: 'Bahan kimia',
    shortTitle: 'Kimia',
    desc: 'Untuk bahan kimia, petrokimia, oil refining, cold storage, perikanan, dan food processing.',
    icon: faFlaskVial,
    accent: 'bg-emerald-400 text-[#071a2d]',
  },
]

const DROPIFY_REASONS = [
  {
    icon: faBolt,
    title: 'Saring seri berdasarkan risiko',
    desc: 'Kirim risiko kerja, jumlah, dan kota tujuan. Admin akan memeriksa seri yang relevan beserta ketersediaannya.',
  },
  {
    icon: faFileShield,
    title: 'Dokumen dan marking per seri dicek',
    desc: 'Standar seperti EN 388, EN 374, atau EN 1149-1 tidak sama untuk semua seri. Admin memeriksa dokumen sebelum nama seri dicantumkan dalam PO.',
  },
  {
    icon: faTag,
    title: 'Harga sesuai seri dan volume',
    desc: 'Harga dihitung berdasarkan seri, ukuran, dan jumlah pembelian. Admin menyiapkan penawaran yang dapat diperiksa tim pengadaan Anda.',
  },
  {
    icon: faTruckFast,
    title: 'Kirim ke pabrik, workshop, atau proyek',
    desc: 'Pesanan dapat dikirim ke berbagai lokasi di seluruh Indonesia dengan ekspedisi yang disesuaikan dengan kota tujuan.',
  },
]

const COMET_TRUST_QUESTIONS: TrustQuestion[] = [
  {
    q: 'Apakah Dropify menjual masker dan sarung tangan COMET?',
    a: 'Ya. Selain sarung tangan kerja COMET di halaman ini, kami juga menyuplai masker COMET Theo. Spesifikasi per tipe masker ada di halaman masker COMET.',
  },
  {
    q: 'Bagaimana cara memilih seri sarung tangan COMET yang tepat?',
    a: 'Kirim risiko utama di area kerja, standar K3 yang diwajibkan, ukuran, dan jumlah. Admin akan menyaring seri yang relevan dan mengirim dokumen yang tersedia.',
  },
  {
    q: 'Apakah dokumen EN 388 atau EN 374 tersedia untuk semua seri?',
    a: 'Tidak. Marking dan dokumen berbeda per seri. Sebutkan standar wajib Anda agar admin hanya menawarkan seri yang dokumennya lengkap.',
  },
  {
    q: 'Bisakah meminta sample untuk uji coba di area kerja?',
    a: 'Bisa ditanyakan untuk seri pilihan. Sample membantu tim memeriksa ukuran, ketangkasan tangan, grip, dan kecocokan dengan proses kerja.',
  },
  {
    q: 'Berapa lama pengiriman COMET dari Surabaya?',
    a: 'Sarung tangan COMET dikirim dari Surabaya. Admin mengonfirmasi waktu penyiapan, lalu memberi estimasi perjalanan berdasarkan kota tujuan dan layanan ekspedisi.',
  },
]

export const COMET_FAQ: FaqItem[] = [
  {
    q: 'Seri COMET mana yang cocok untuk pekerjaan saya?',
    a: 'Pilih dari risiko utama di area kerja. CG 801, CG 803, CG 805, dan CG 813 dapat dipertimbangkan untuk pekerjaan presisi atau elektronik. CG 905 dan Impact Glove ditujukan untuk kondisi berminyak atau risiko benturan. CG 835 dan CG 855 berkaitan dengan risiko sayatan, sedangkan NF 1513 dan CG 656 untuk kebutuhan kimia tertentu. Tim K3 tetap perlu mencocokkan seri dengan penilaian risiko dan SOP perusahaan.',
  },
  {
    q: 'Apakah semua sarung tangan COMET memiliki standar yang sama?',
    a: 'Tidak. Standar dan marking berbeda menurut seri. EN 388 berlaku pada seri untuk risiko mekanis, EN 1149-1 pada CG 813 ESD, serta EN 374 pada NF 1513 dan CG 656. Minta dokumen seri yang dipilih sebelum mencantumkan nama produk dalam PO.',
  },
  {
    q: 'Berapa harga dan minimum order sarung tangan COMET?',
    a: 'Harga dan minimum order mengikuti seri, ukuran, jumlah, serta stok saat pemesanan. Kirim nama seri, pembagian ukuran, jumlah kebutuhan, dan kota tujuan agar admin dapat memberikan harga serta ongkir yang sesuai.',
  },
  {
    q: 'Apakah tersedia sample untuk pengujian di area kerja?',
    a: 'Bisa ditanyakan untuk seri yang Anda pilih. Sample membantu tim memeriksa ukuran, ketangkasan tangan, grip, dan kecocokan dengan proses kerja sebelum membeli dalam jumlah besar.',
  },
  {
    q: 'Apakah kode EN 374 berarti aman untuk seluruh bahan kimia?',
    a: 'Tidak. Kecocokan sarung tangan kimia bergantung pada nama zat, konsentrasi, suhu, lama kontak, dan data permeasi. Kirim nama bahan atau SDS agar tim K3 dan admin dapat memeriksa seri serta dokumennya sebelum digunakan.',
  },
  {
    q: 'Bagaimana cara meminta quotation dan dokumen COMET?',
    a: 'Kirim nama seri atau jenis pekerjaan, risiko utama, standar wajib, ukuran, jumlah, dan kota tujuan melalui WhatsApp. Admin akan mengecek stok, harga, minimum order, serta dokumen produk yang tersedia.',
  },
]

const COMET_SCHEMA_PRODUCTS = COMET_GLOVE_DEFINITIONS.map((product, index) => ({
  '@type': 'ListItem',
  position: index + 1,
  item: {
    '@type': 'Thing',
    name: product.name,
    sku: product.sku,
    description: `${product.construction}. ${product.useCase}. ${product.standard}.`,
    image: product.image,
    url: absoluteUrl(`/produk/${product.slug}`),
    brand: { '@type': 'Brand', name: 'COMET' },
  },
}))

function resolveCometProducts(products: StorefrontProduct[]): CometProduct[] {
  return COMET_GLOVE_DEFINITIONS.map((definition) => {
    const product = products.find(
      (item) => item.sku === definition.sku || item.slug === definition.slug,
    )

    return {
      ...definition,
      slug: product?.slug || definition.slug,
      image: product
        ? getProductPrimaryImage(product, { width: 720, format: 'webp', quality: 74 })
        : definition.image,
    }
  })
}

export const Route = createFileRoute('/sarung-tangan-comet')({
  loader: async () => {
    const [config, categories, response, proofImages, newsPosts] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontCategories().catch(() => [] as StorefrontCategory[]),
      listStorefrontProducts({ search: 'COMET', per_page: 100 }).catch(() => null),
      getBrandProofImages(),
      listDashboardNewsPosts().catch(() => []),
    ])

    return {
      config,
      categories,
      products: resolveCometProducts(response?.items ?? []),
      proofImages,
      newsPosts,
    }
  },
  head: () => {
    const title = brandTitle('Sarung Tangan COMET untuk Kebutuhan Industri')
    const description =
      'Sarung tangan COMET 14 seri untuk presisi, ESD, minyak, benturan, sayat & kimia. Quotation & dokumen per seri. Cek stok via WhatsApp.'
    const heroImage = COMET_GLOVE_DEFINITIONS.find((item) => item.sku === 'COMET-IMPACT')?.image
      ?? COMET_GLOVE_DEFINITIONS[0].image

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { name: 'robots', content: INDEXABLE_ROBOTS },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl(PAGE_PATH) },
        { property: 'og:locale', content: 'id_ID' },
        { property: 'og:image', content: heroImage },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: heroImage },
      ],
      links: [{ rel: 'canonical', href: absoluteUrl(PAGE_PATH) }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildBrandLandingSchema({ brandName: 'COMET', pageName: title, description, path: PAGE_PATH })),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: 'Beranda', path: '/' },
              { name: 'Merek', path: '/merek' },
              { name: 'Sarung Tangan COMET', path: PAGE_PATH },
            ]),
          ),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildFaqSchema([...COMET_FAQ, ...COMET_TRUST_QUESTIONS])),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Produk Sarung Tangan COMET di Dropify',
            itemListElement: COMET_SCHEMA_PRODUCTS,
          }),
        },
      ],
    }
  },
  component: CometRoute,
})

function CometRoute() {
  const { config, categories, products, proofImages, newsPosts } = Route.useLoaderData()
  return <CometLandingPage config={config} categories={categories} products={products} proofImages={proofImages} newsPosts={newsPosts} />
}

function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

function WhatsAppButton({
  message,
  label,
  placement,
  itemName,
  className,
}: {
  message: string
  label: string
  placement: string
  itemName?: string
  className: string
}) {
  return (
    <a
      href={waLink(message)}
      target="_blank"
      rel="noopener noreferrer"
      {...whatsAppCtaProps('landing_page', {
          cta_placement: placement,
          lp_category: PAGE_CATEGORY,
          ...(itemName
            ? {
                lead_item_name: itemName,
                items: [{ item_id: itemName, item_name: itemName, item_category: PAGE_CATEGORY }],
              }
            : {}),
        })}
      className={className}
    >
      <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5 flex-shrink-0" />
      {label}
    </a>
  )
}

function SectionLabel({ children, light = false }: { children: string; light?: boolean }) {
  return (
    <p className={`text-xs font-black uppercase tracking-[0.22em] ${light ? 'text-cyan-300' : 'text-blue-700'}`}>
      {children}
    </p>
  )
}

function ProductCard({ product }: { product: CometProduct }) {
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white transition-all hover:-translate-y-1 hover:border-blue-300 hover:shadow-xl">
      <a
        href={`/produk/${product.slug}`}
        aria-label={`Lihat detail ${product.name}`}
        onClick={() => trackSelectItem({ item_id: product.sku, item_name: product.name, item_category: PAGE_CATEGORY }, 'COMET brand landing')}
        className="relative block overflow-hidden bg-slate-50"
      >
        <img
          src={product.image}
          alt={product.name}
          width="560"
          height="560"
          loading="lazy"
          decoding="async"
          className="aspect-square w-full object-contain p-5 transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-4 top-4 rounded-full bg-[#071a2d] px-3 py-1 text-[11px] font-black tracking-wide text-cyan-300">{product.sku}</span>
      </a>
      <div className="flex flex-1 flex-col p-5">
        <p className="text-xs font-black uppercase tracking-[0.16em] text-blue-700">{product.construction}</p>
        <h3 className="mt-2 text-lg font-black tracking-tight">
          <a
            href={`/produk/${product.slug}`}
            onClick={() => trackSelectItem({ item_id: product.sku, item_name: product.name, item_category: PAGE_CATEGORY }, 'COMET brand landing')}
            className="transition-colors hover:text-blue-700"
          >
            {product.shortName}
          </a>
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">{product.useCase}</p>
        <div className="mt-4 space-y-2 rounded-2xl bg-slate-50 p-4 text-xs leading-relaxed text-slate-600">
          <p><strong className="text-slate-900">Standar:</strong> {product.standard}</p>
          <p><strong className="text-slate-900">Ukuran:</strong> {product.sizes}</p>
        </div>
        <WhatsAppButton
          message={`Halo Dropify, saya mau cek COMET ${product.shortName} (${product.sku}). Kebutuhan: ___ pasang/lusin. Pembagian ukuran: ___. Kota tujuan: ___. Bisa kirim stok, harga, dan dokumennya?`}
          label={`Tanya ${product.shortName}`}
          placement="product_card"
          itemName={product.name}
          className="mt-5 inline-flex items-center justify-center gap-2 rounded-xl bg-green-700 px-4 py-3 text-sm font-black text-white transition-colors hover:bg-green-800"
        />
      </div>
    </article>
  )
}

export function CometLandingPage({
  config = null,
  categories = [],
  products = resolveCometProducts([]),
  proofImages = [],
  newsPosts = [],
}: {
  config?: StorefrontConfig | null
  categories?: StorefrontCategory[]
  products?: CometProduct[]
  proofImages?: string[]
  newsPosts?: import('../lib/dropify').StorefrontBlogPost[]
}) {
  const heroMessage =
    'Halo Dropify, saya mencari sarung tangan COMET. Pekerjaan: ___. Risiko utama: ___. Standar wajib: ___. Kebutuhan: ___. Kota tujuan: ___. Bisa bantu pilihkan seri dan buat quotation?'
  const featuredSkus = ['COMET-CG813', 'COMET-IMPACT', 'COMET-CG835', 'COMET-NF1513']
  const featuredProducts = featuredSkus
    .map((sku) => products.find((product) => product.sku === sku))
    .filter((product): product is CometProduct => Boolean(product))

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950">
      <div className="bg-cyan-300 px-4 py-2 text-center text-xs font-black text-[#071a2d] sm:text-sm">
        14 seri · MOQ per seri · Quotation & dokumen tersedia
      </div>

      <header className="border-b border-white/10 bg-[#071a2d] text-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
          <a href="/" aria-label="Kembali ke Dropify">
            <LogoLockup size="sm" invert />
          </a>
          <WhatsAppButton
            message={heroMessage}
            label="Periksa seri COMET"
            placement="header"
            className="inline-flex items-center gap-2 rounded-full border border-cyan-300/60 px-4 py-2.5 text-sm font-bold text-cyan-200 transition-colors hover:bg-cyan-300 hover:text-[#071a2d]"
          />
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden bg-[#071a2d] text-white">
          <div aria-hidden className="absolute -right-32 -top-32 h-[34rem] w-[34rem] rounded-full border-[100px] border-blue-500/10" />
          <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-14 md:py-20 lg:grid-cols-[1.02fr_0.98fr]">
            <div>
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full bg-cyan-300 px-3 py-1 text-xs font-black uppercase tracking-wider text-[#071a2d]">COMET Industrial Gloves</span>
                <span className="rounded-full border border-white/20 px-3 py-1 text-xs font-bold text-slate-300">EN 388 · EN 374 · EN 1149-1</span>
              </div>
              <h1 className="mt-6 max-w-3xl text-4xl font-black leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
                Pilih sarung tangan COMET sesuai <span className="text-cyan-300">risiko di area kerja.</span>
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg">
                Seri COMET mencakup kebutuhan presisi, ESD, area berminyak, benturan, sayatan, dan bahan kimia. Kirim kondisi kerja, ukuran, serta standar yang diwajibkan agar admin dapat menyaring seri dan dokumen yang relevan.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <WhatsAppButton
                  message={heroMessage}
                  label="Minta rekomendasi & quotation"
                  placement="hero_primary"
                  className="inline-flex items-center justify-center gap-2.5 rounded-xl bg-green-700 px-6 py-4 text-base font-black text-white shadow-lg shadow-black/20 transition-colors hover:bg-green-800"
                />
                <a href="#pilih-risiko" className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 px-6 py-4 text-sm font-black text-white transition-colors hover:bg-white/10">
                  Pilih dari risiko kerja
                  <FontAwesomeIcon icon={faArrowRight} className="h-4 w-4" />
                </a>
              </div>
              <p className="mt-4 text-sm text-slate-400">Admin akan mengonfirmasi MOQ dan harga untuk setiap seri. Ukuran tersedia mulai S sampai XXL, bergantung pada model.</p>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {featuredProducts.map((product, index) => (
                <article
                  key={product.sku}
                  data-hero-product-visual
                  className={`overflow-hidden rounded-3xl border border-white/10 bg-white p-3 shadow-xl ${index % 2 === 1 ? 'translate-y-5' : '-translate-y-1'}`}
                >
                  <img src={product.image} alt={product.name} width="480" height="480" fetchPriority={index < 2 ? 'high' : undefined} decoding="async" className="aspect-square w-full object-contain" />
                  <div className="px-2 pb-2 pt-3 text-[#071a2d]">
                    <span className="text-sm font-black">{product.shortName}</span>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-slate-200 bg-white py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-[0.78fr_1.22fr] lg:gap-16">
            <div>
              <SectionLabel>Kenali lini produknya</SectionLabel>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Apa itu sarung tangan COMET?</h2>
            </div>
            <div className="space-y-5 text-base leading-relaxed text-slate-600 md:text-lg">
              <p>
                COMET adalah lini sarung tangan kerja industri dengan konstruksi dan tingkat perlindungan yang berbeda pada setiap seri. Pilihannya mencakup rajut tipis untuk pekerjaan presisi, PU, nitrile, HPPE, aramid, PVC, pelindung benturan TPR, serta seri untuk kebutuhan kimia.
              </p>
              <p>
                Nama merek saja belum cukup untuk membuat PO. Cocokkan bahaya kerja dengan material sarung tangan, kode standar, ukuran, dan batas pemakaiannya. Dokumen serta marking perlu diperiksa pada seri yang akan dibeli.
              </p>
              <div className="flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm leading-relaxed text-amber-950">
                <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                <p><strong>Catatan K3:</strong> rekomendasi di halaman ini adalah panduan awal. Pemilihan akhir mengikuti penilaian risiko, SDS bahan, SOP, dan persetujuan tim K3 perusahaan.</p>
              </div>
            </div>
          </div>
        </section>

        <BrandEasyOrder newsPosts={newsPosts} />

        <section id="pilih-risiko" className="scroll-mt-6 bg-slate-50 py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4">
            <div className="max-w-3xl">
              <SectionLabel>Peta pemilihan</SectionLabel>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Risiko apa yang paling dominan?</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">Mulai dari bahaya utama, lalu periksa bahaya tambahan. Satu pekerjaan dapat membutuhkan lebih dari satu pengujian.</p>
            </div>
            <div className="mt-9 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {RISK_GROUPS.map((group) => (
                <a key={group.key} href={`#${group.anchor}`} className="group rounded-3xl border border-slate-200 bg-white p-5 transition-all hover:-translate-y-1 hover:border-blue-300 hover:shadow-lg">
                  <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${group.accent}`}>
                    <FontAwesomeIcon icon={group.icon} className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 text-xl font-black tracking-tight">{group.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{group.desc}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-black text-blue-700">Lihat seri <FontAwesomeIcon icon={faArrowRight} className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl space-y-16 px-4">
            {RISK_GROUPS.map((group) => {
              const groupProducts = products.filter((product) => product.risk === group.key)
              const productGridClass = groupProducts.length === 4
                ? 'lg:grid-cols-4'
                : groupProducts.length > 2
                  ? 'lg:grid-cols-3'
                  : 'lg:grid-cols-2'
              return (
                <div key={group.key} id={group.anchor} className="scroll-mt-6">
                  <div className="flex flex-col justify-between gap-5 border-b border-slate-200 pb-6 md:flex-row md:items-end">
                    <div className="max-w-3xl">
                      <div className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${group.accent}`}>
                        <FontAwesomeIcon icon={group.icon} className="h-5 w-5" />
                      </div>
                      <h2 className="mt-4 text-3xl font-black tracking-tight">{group.title}</h2>
                      <p className="mt-3 text-base leading-relaxed text-slate-600">{group.desc}</p>
                    </div>
                    <p className="text-sm font-bold text-slate-500">{groupProducts.length} seri tersedia</p>
                  </div>
                  <div className={`mt-7 grid gap-5 sm:grid-cols-2 ${productGridClass}`}>
                    {groupProducts.map((product) => <ProductCard key={product.sku} product={product} />)}
                  </div>
                </div>
              )
            })}
          </div>
        </section>

        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
            <div>
              <SectionLabel>Data pemesanan</SectionLabel>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Seri COMET diperiksa berdasarkan pekerjaan dan dokumen yang diwajibkan</h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">Kirim jenis pekerjaan, risiko utama, ukuran, jumlah, dan dokumen wajib. Admin akan membandingkan seri, memeriksa stok, dan menyiapkan quotation.</p>
              <WhatsAppButton
                message={heroMessage}
                label="Chat admin COMET"
                placement="why_dropify"
                className="mt-6 inline-flex items-center gap-2 rounded-xl bg-green-700 px-5 py-3.5 text-sm font-black text-white transition-colors hover:bg-green-800"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {DROPIFY_REASONS.map((item) => (
                <article key={item.title} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#071a2d] text-cyan-300">
                    <FontAwesomeIcon icon={item.icon} className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-lg font-black tracking-tight">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-slate-200 bg-slate-50 py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4">
            <div className="text-center">
              <SectionLabel>FAQ COMET</SectionLabel>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Sebelum memilih seri COMET</h2>
            </div>
            <div className="mt-9 divide-y divide-slate-200 border-y border-slate-200">
              {COMET_FAQ.map((item) => (
                <details key={item.q} className="group py-5">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-5 text-left font-black text-slate-900">
                    {item.q}
                    <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 flex-shrink-0 text-blue-700 transition-transform group-open:rotate-180" />
                  </summary>
                  <p className="mt-3 max-w-3xl pr-8 text-sm leading-relaxed text-slate-600 md:text-base">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-cyan-300 py-14 text-[#071a2d]">
          <div className="mx-auto flex max-w-7xl flex-col justify-between gap-7 px-4 lg:flex-row lg:items-center">
            <div className="max-w-3xl">
              <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em]"><FontAwesomeIcon icon={faFileShield} className="h-4 w-4" />Siap cek kebutuhan Anda</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight md:text-4xl">Kirim risiko kerja dan standar wajibnya.</h2>
              <p className="mt-3 text-sm leading-relaxed text-[#173a50] md:text-base">Admin akan membantu menyaring seri COMET, ukuran, stok, harga, dan dokumen yang tersedia.</p>
            </div>
            <WhatsAppButton
              message={heroMessage}
              label="Minta quotation COMET"
              placement="final_cta"
              className="inline-flex flex-shrink-0 items-center justify-center gap-2.5 rounded-xl bg-[#071a2d] px-7 py-4 text-base font-black text-white transition-colors hover:bg-slate-800"
            />
          </div>
        </section>

        <BrandTrustSections brandName="COMET" itemName="sarung tangan COMET" images={proofImages} questions={COMET_TRUST_QUESTIONS} />
        <GloveBrandAlternatives currentPath={PAGE_PATH} />
      </main>

      <Footer config={config} categories={categories} />
    </div>
  )
}
